/****************************************************************************
 * COPYRIGHT (c) 2024 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import APIManager from "../../ApiManager/ApiManager";

import { getConfig } from "../../../config";
import {
  FETCH_APPLICATION_DETAILS_REQUEST,
  FETCH_APPLICATION_DETAILS_SUCCESS,
  FETCH_APPLICATION_DETAILS_FAILURE,
  FETCH_APPLICATION_DETAILS_SCREENSHOTS,
  FETCH_APPLICATION_DETAILS_ADDDITIONAL_DOCUMENT,
} from "./ApplicationDetailsTypes";
import { COMMON_ERROR } from "../ApplicationDetailsError/ApplicationDetailsErrorTypes";

export const fetchApplicationDetailsRequest = () => {
  return {
    type: FETCH_APPLICATION_DETAILS_REQUEST,
  };
};

export const fetchApplicationSuccess = (appData) => {
  return {
    type: FETCH_APPLICATION_DETAILS_SUCCESS,
    payload: appData,
  };
};

export const fetchApplicationFailure = (error) => {
  return {
    type: FETCH_APPLICATION_DETAILS_FAILURE,
    payload: error,
  };
};

export const fetchApplicationDetailsCreenShots = (
  newSubmissionData,
  screenShots
) => {
  return {
    type: FETCH_APPLICATION_DETAILS_SCREENSHOTS,
    payload: { newSubmissionData, screenShots },
  };
};

export const fetchApplicationDetailsDocuments = (newSubmissionData) => {
  return {
    type: FETCH_APPLICATION_DETAILS_ADDDITIONAL_DOCUMENT,
    payload: newSubmissionData,
  };
};



// Add new action creators
export const putSubmissionRequest = () => ({
  type: "PUT_SUBMISSION_REQUEST",
});

export const putSubmissionSuccess = (data) => ({
  type: "PUT_SUBMISSION_SUCCESS",
  payload: data,
});

export const putSubmissionFailure = (error) => ({
  type: "PUT_SUBMISSION_FAILURE",
  payload: error,
});

export const fetchSubmissionDetails = (applicationId, submissionId) => {
  return async function (dispatch) {
    dispatch(fetchApplicationDetailsRequest());
    try {
      const data = await APIManager.getSubmission(applicationId, submissionId);
      dispatch(fetchApplicationSuccess(data));
    } catch (error) {
      console.error("error====>", error);
      dispatch(fetchApplicationFailure(error));
      dispatch(commonError(error, FETCH_APPLICATION_DETAILS_FAILURE));
    }
  };
};

export const fetchSubmissionChangelog = async (applicationId, submissionId) => {
  const data = await APIManager.getSubmissionChangelog(applicationId, submissionId);
  return data;
};

export const fetchAdminSubmissionChangelog = async (applicationId, submissionId) => {
  const data = await APIManager.getAdminSubmissionChangelog(applicationId, submissionId);
  return data;
};

// Debounce mechanism to prevent rapid successive calls
let fetchTimeout = null;

export const fetchPortfolioContentDetails = (
  portfolioId,
  contenId,
  forceRefresh = false
) => {
  return async function (dispatch, getState) {
    // Check if data is already being fetched
    const currentState = getState();
    const { loading } = currentState.submissionDetails;

    // Don't fetch if already loading, unless force refresh is requested
    if (loading && !forceRefresh) {
      console.log(
        "Portfolio content details already loading, skipping API call"
      );
      return;
    }

    // Clear any pending debounced call
    if (fetchTimeout) {
      clearTimeout(fetchTimeout);
    }

    // For force refresh, call immediately; otherwise debounce
    if (forceRefresh) {
      return executePortfolioContentFetch(dispatch, portfolioId, contenId);
    } else {
      // Debounce regular calls by 100ms to prevent rapid successive calls
      return new Promise((resolve, reject) => {
        fetchTimeout = setTimeout(async () => {
          try {
            const result = await executePortfolioContentFetch(
              dispatch,
              portfolioId,
              contenId
            );
            resolve(result);
          } catch (error) {
            reject(error);
          }
        }, 100);
      });
    }
  };
};

// Helper function to actually execute the fetch
const executePortfolioContentFetch = async (
  dispatch,
  portfolioId,
  contenId
) => {
  dispatch(fetchApplicationDetailsRequest());
  try {
    const data = await APIManager.getPortfolioContentDetails(
      portfolioId,
      contenId
    );
    dispatch(fetchApplicationSuccess(data));
    return data;
  } catch (error) {
    dispatch(fetchApplicationFailure(error));
    dispatch(commonError(error, FETCH_APPLICATION_DETAILS_FAILURE));
    throw error;
  }
};

export const putSubmissionDetails = (
  applicationId,
  submissionId,
  newSubmissionData
) => {
  return async function (dispatch) {
    dispatch(putSubmissionRequest());
    try {
      const data =
        getConfig().REACT_APP_PORTAL === "domain-approver" ||
        getConfig().REACT_APP_PORTAL === "global-admin"
          ? await APIManager.putDomainApproverSubmission(
              applicationId,
              submissionId,
              newSubmissionData
            )
          : await APIManager.putSubmission(
              applicationId,
              submissionId,
              newSubmissionData
            );

      dispatch(fetchApplicationSuccess(data));
    } catch (error) {
      dispatch(putSubmissionFailure(error));
      dispatch(commonError(error, FETCH_APPLICATION_DETAILS_FAILURE));
    }
  };
};

export const putSubmissionDetailsGarageMode = (
  applicationId,
  submissionId,
  newSubmissionData
) => {
  return async function (dispatch) {
    dispatch(putSubmissionRequest());
    try {
      const data = await APIManager.putGlobalAdminGarageModeSubmission(
        applicationId,
        submissionId,
        newSubmissionData
      );

      dispatch(fetchApplicationSuccess(data));
    } catch (error) {
      dispatch(putSubmissionFailure(error));
      dispatch(commonError(error, FETCH_APPLICATION_DETAILS_FAILURE));
    }
  };
};

export const postSubmissionDetails = (
  applicationId,
  submissionId,
  status,
  forceUninstall
) => {
  return async function (dispatch) {
    dispatch(fetchApplicationDetailsRequest());
    try {
      const data = await APIManager.sendSubmissionForProductionDelistAndRelist(
        applicationId,
        submissionId,
        status,
        forceUninstall
      );
      dispatch(fetchApplicationSuccess(data));
      return data;
    } catch (error) {
      dispatch(fetchApplicationFailure(error));
      dispatch(commonError(error, FETCH_APPLICATION_DETAILS_FAILURE));
      throw error;
    }
  };
};
export const postSubmissionDetailsDomainApprover = (
  applicationId,
  submissionId,
  status,
  file,
  comment = "",
  reportLink = ""
) => {
  return async function (dispatch) {
    dispatch(fetchApplicationDetailsRequest());
    try {
      const data = await APIManager.sendSubmissionForProductionDomainApprover(
        applicationId,
        submissionId,
        status,
        comment,
        file,
        reportLink
      );
      dispatch(fetchApplicationSuccess(data));
    } catch (error) {
      dispatch(fetchApplicationFailure(error));
      dispatch(commonError(error, FETCH_APPLICATION_DETAILS_FAILURE));
    }
  };
};
export const postSubmissionDetailsDomainApproverForProduction = (
  applicationId,
  submissionId,
  status,
  file,
  comment = "",
  reportLink = ""
) => {
  return async function (dispatch) {
    dispatch(fetchApplicationDetailsRequest());
    try {
      const data =
        await APIManager.postSubmissionDetailsDomainApproverForProduction(
          applicationId,
          submissionId,
          status,
          file,
          comment,
          reportLink
        );
      dispatch(fetchApplicationSuccess(data));
    } catch (error) {
      dispatch(fetchApplicationFailure(error));
      dispatch(commonError(error, FETCH_APPLICATION_DETAILS_FAILURE));
    }
  };
};

export const fetchUploadIconDetails = (applicationId, submissionId, image) => {
  return async function (dispatch) {
    dispatch(fetchApplicationDetailsRequest());
    try {
      const data = await APIManager.uploadIconFile(
        applicationId,
        submissionId,
        image
      );
      dispatch(fetchApplicationSuccess(data));
    } catch (error) {
      dispatch(fetchApplicationFailure(error));
      dispatch(commonError(error, FETCH_APPLICATION_DETAILS_FAILURE));
    }
  };
};

export const fetchSubmissionsForCreateNewRelease = (appId) => {
  return async function (dispatch) {
    dispatch(fetchApplicationDetailsRequest());
    try {
      const data = await APIManager.callSubmissionsForCreateNewRelease(appId);
      dispatch(fetchApplicationSuccess(data));
    } catch (error) {
      dispatch(fetchApplicationFailure(error));
      dispatch(commonError(error, FETCH_APPLICATION_DETAILS_FAILURE));
    }
  };
};

export const fetchDeleteApplication = (appId, submissionId) => {
  return async function (dispatch) {
    dispatch(fetchApplicationDetailsRequest());
    try {
      await APIManager.deleteApplication(appId, submissionId);
      dispatch(fetchApplicationSuccess({}));
    } catch (error) {
      dispatch(fetchApplicationFailure(error));
      dispatch(commonError(error, FETCH_APPLICATION_DETAILS_FAILURE));
    }
  };
};

export const fetchDeleteDescriptions = (appId, submissionId, languageArray) => {
  return async function (dispatch) {
    dispatch(fetchApplicationDetailsRequest());
    try {
      const data = await APIManager.deleteDescriptions(
        appId,
        submissionId,
        languageArray
      );
      dispatch(fetchApplicationSuccess(data));
    } catch (error) {
      dispatch(fetchApplicationFailure(error));
      dispatch(commonError(error, FETCH_APPLICATION_DETAILS_FAILURE));
    }
  };
};

export const patchPortfolioCountryDetails = (
  portfolioId,
  contentId,
  tags,
  reasonForExclusion,
  isQuietUpdate = false
) => {
  return async (dispatch) => {
    // Only set loading state for non-quiet updates
    if (!isQuietUpdate) {
      dispatch(fetchApplicationDetailsRequest());
    }

    try {
      const response = await APIManager.updatePortfolioContentDetails(
        portfolioId,
        contentId,
        tags,
        reasonForExclusion
      );

      // For quiet updates (like MMY), don't dispatch success to avoid triggering other components
      if (!isQuietUpdate) {
        dispatch(fetchApplicationSuccess(response));
      }

      return response;
    } catch (error) {
      dispatch(fetchApplicationFailure(error));
      dispatch(commonError(error, FETCH_APPLICATION_DETAILS_FAILURE));
      throw error;
    }
  };
};

export const patchPortfolioPreInstallRequest = () => ({
  type: "PATCH_PORTFOLIO_PREINSTALL_REQUEST",
});

export const patchPortfolioPreInstallSuccess = (data) => ({
  type: "PATCH_PORTFOLIO_PREINSTALL_SUCCESS",
  payload: data,
});

export const patchPortfolioPreInstallFailure = (error) => ({
  type: "PATCH_PORTFOLIO_PREINSTALL_FAILURE",
  payload: error,
});
export const patchPortfolioPreInstall = (portfolioId, contentId, payload) => {
  return async (dispatch) => {
    dispatch(patchPortfolioPreInstallRequest());
    try {
      const response =
        await APIManager.updatePortfolioContentDetailsForPreInstall(
          portfolioId,
          contentId,
          payload
        );
      dispatch(patchPortfolioPreInstallSuccess(response));
      return response;
    } catch (error) {
      dispatch(patchPortfolioPreInstallFailure(error));
      dispatch(commonError(error, FETCH_APPLICATION_DETAILS_FAILURE));
      throw error;
    }
  };
};

export const patchPortfolioAppApproveRejectDetails = (
  portfolioId,
  contenId,
  payloadData
) => {
  return async function (dispatch) {
    dispatch(fetchApplicationDetailsRequest());
    try {
      await APIManager.approvAndRejectApps(
        portfolioId,
        contenId,
        payloadData
      );
      dispatch(fetchApplicationSuccess());
    } catch (error) {
      dispatch(fetchApplicationFailure(error));
      dispatch(commonError(error, FETCH_APPLICATION_DETAILS_FAILURE));
    }
  };
};
export const patchDomainApproverAppApproveRejectDetails = (
  contenId,
  payloadData
) => {
  return async function (dispatch) {
    dispatch(fetchApplicationDetailsRequest());
    try {
      const data = await APIManager.approvAndRejectDomainApproverThirdPartyApps(
        contenId,
        payloadData
      );
      dispatch(fetchApplicationSuccess(data));
      return data;
    } catch (error) {
      dispatch(fetchApplicationFailure(error));
      dispatch(commonError(error, FETCH_APPLICATION_DETAILS_FAILURE));
    }
  };
};
export const commonError = (error, errorType) => {
  return {
    type: COMMON_ERROR,
    payload: {
      message: error.message,
      errorType: errorType,
    },
  };
};
